<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { ref, computed, watch, nextTick, type Ref } from 'vue'
import GbDialog from '@/components/GbDialog.vue'
import GbButton from '@/components/GbButton.vue'
import { useUserStore } from '@/authentication/user.store'
import { generateAttachmentUploadUrl, uploadFileToS3, saveFileAttachment, generateAttachmentFileUpdateUrl, updateFileAttachment } from '@/library/api'
import { ATTACHMENT_TYPE } from '@/library/utils/urlUtils'
import { validateFileSize, validateFileContents, sanitizeFileName, sanitizeTitle, ALLOWED_FILE_ACCEPT } from '@/library/utils/fileUtils'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import type { LibraryContentObject, LibraryAttachment } from '@/library/library_types'

const props = defineProps<{
  externalOpen: boolean
  folderUuid: string
  folderName?: string
  editAttachment?: LibraryContentObject | null
}>()

const emit = defineEmits<{
  'update:externalOpen': [value: boolean]
  saved: []
  fileError: [message: string]
}>()

const userStore = useUserStore()
const { userId } = storeToRefs(userStore)

const { getReactiveEventAttributes } = useLibraryFolderAnalytics()
const analytics = getAnalyticsTracker().getChildContext(getReactiveEventAttributes())

const visibleRef: Ref<boolean> = ref(false)

const selectedFile = ref<File | null>(null)
const fileTitle = ref('')
const errorMessage = ref('')
const isSubmitting = ref(false)

const fileInputRef = ref<HTMLInputElement | null>(null)
const triggerInputRef = ref<HTMLInputElement | null>(null)

const isEditMode = computed(() => !!props.editAttachment)
const editContentUuid = computed(() => props.editAttachment?.content_uuid ?? '')
const editCollectionUuid = computed(() => props.editAttachment?.collection_uuid ?? props.folderUuid)

const dialogId = computed(() => `upload-file-modal-${props.folderUuid || 'unknown'}`)

const isSaveDisabled = computed((): boolean => {
  if (isSubmitting.value || !!errorMessage.value) return true
  if (isEditMode.value) {
    return !selectedFile.value && fileTitle.value.trim() === (props.editAttachment?.content_title ?? '').trim()
  }
  return !selectedFile.value
})

watch(
  () => props.externalOpen,
  (newVal) => {
    if (!newVal) {
      visibleRef.value = false
    }
  }
)

watch(
  () => visibleRef.value,
  (newVal) => {
    emit('update:externalOpen', newVal)
  }
)

watch(selectedFile, (file) => {
  if (file && !isEditMode.value) {
    fileTitle.value = file.name
  }
})

async function onTriggerFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  input.value = ''
  if (!file) {
    emit('update:externalOpen', false)
    return
  }
  selectedFile.value = file
  visibleRef.value = true
  errorMessage.value = validateFileSize(file) ?? await validateFileContents(file) ?? ''
}

async function onFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  if (file) {
    selectedFile.value = file
    errorMessage.value = validateFileSize(file) ?? await validateFileContents(file) ?? ''
  }
}

function triggerFileInput(): void {
  fileInputRef.value?.click()
}

async function submitUpload(): Promise<void> {
  if (!selectedFile.value) {
    errorMessage.value = 'Please select a file to upload.'
    return
  }

  const sanitizedFileName = sanitizeFileName(selectedFile.value.name)
  // resolvedTitle preserves spaces for display; sanitizedTitle is used in API/S3 headers
  // where spaces would break the presigned URL signature
  const resolvedTitle = fileTitle.value.trim() || sanitizedFileName
  const sanitizedTitle = sanitizeTitle(resolvedTitle)

  const uploadUrlResponse = await generateAttachmentUploadUrl({
    folderUuid: props.folderUuid,
    fileUuid: crypto.randomUUID(),
    contentType: selectedFile.value.type || 'application/octet-stream',
    fileTitle: sanitizedTitle,
    fileName: sanitizedFileName,
    userUuid: userId.value,
  })

  await uploadFileToS3(uploadUrlResponse.url, selectedFile.value, uploadUrlResponse)

  const computedExtension = sanitizedFileName.includes('.') ? sanitizedFileName.split('.').pop()!.toLowerCase() : ''
  const fileExtension = uploadUrlResponse.file_extension?.toLowerCase() || computedExtension

  // Use the original resolvedTitle (with spaces) for the human-readable DB record
  await saveFileAttachment({
    collectionUuid: props.folderUuid,
    contentUuid: uploadUrlResponse.file_uuid,
    fileTitle: resolvedTitle,
    fileType: selectedFile.value.type || 'application/octet-stream',
    fileExtension,
    contributor: userId.value,
  })

  const fileTitleProvided = !!fileTitle.value.trim() && fileTitle.value.trim() !== selectedFile.value.name
  
  analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.CLICKED_ADD_ATTACHMENT, {
    attachment_type: ATTACHMENT_TYPE.FILE,
    attachment_uuid: uploadUrlResponse.file_uuid,
    file_title_provided: fileTitleProvided,
    file_type: fileExtension,
  })
}

async function submitEdit(): Promise<void> {
  const resolvedTitle = fileTitle.value.trim() || (props.editAttachment?.content_title ?? '')

  if (selectedFile.value) {
    const sizeError = validateFileSize(selectedFile.value)
    if (sizeError) {
      errorMessage.value = sizeError
      return
    }
    const contentsError = await validateFileContents(selectedFile.value)
    if (contentsError) {
      errorMessage.value = contentsError
      return
    }

    const sanitizedFileName = sanitizeFileName(selectedFile.value.name)
    const fileExtension = sanitizedFileName.includes('.') ? sanitizedFileName.split('.').pop()!.toLowerCase() : ''

    // sanitizedTitle replaces spaces so the value matches what the backend signed into the presigned URL
    const sanitizedTitle = sanitizeTitle(resolvedTitle)
    const updateUrlResponse = await generateAttachmentFileUpdateUrl({
      folderUuid: editCollectionUuid.value,
      contentUuid: editContentUuid.value,
      contentType: selectedFile.value.type || 'application/octet-stream',
      fileTitle: sanitizedTitle,
      fileName: sanitizedFileName,
      userUuid: userId.value,
    })

    await uploadFileToS3(updateUrlResponse.url, selectedFile.value, {
      ...updateUrlResponse,
      title: sanitizedTitle,
      file_name: updateUrlResponse.file_name || sanitizedFileName,
      file_extension: fileExtension,
    })

    await updateFileAttachment({
      collectionUuid: editCollectionUuid.value,
      contentUuid: editContentUuid.value,
      fileTitle: resolvedTitle,
      fileName: sanitizedFileName,
      fileType: selectedFile.value.type || 'application/octet-stream',
      fileExtension,
      userUuid: userId.value,
    })
  } else {
    await updateFileAttachment({
      collectionUuid: editCollectionUuid.value,
      contentUuid: editContentUuid.value,
      fileTitle: resolvedTitle,
      fileName: '',
      fileType: (props.editAttachment?.content_body as LibraryAttachment)?.file_type ?? '',
      fileExtension: (props.editAttachment?.content_body as LibraryAttachment)?.file_extension ?? '',
      userUuid: userId.value,
    })
  }

  analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.SUBMITTED_ATTACHMENT_EDIT, {
    attachment_type: ATTACHMENT_TYPE.FILE,
    attachment_uuid: editContentUuid.value,
    is_edited_file: !!selectedFile.value,
  })
}

async function submit(): Promise<void> {
  errorMessage.value = ''

  if (isSubmitting.value) {
    return
  }

  if (!isEditMode.value) {
    if (!selectedFile.value) {
      errorMessage.value = 'Please select a file to upload.'
      return
    }

    const sizeError = validateFileSize(selectedFile.value)
    if (sizeError) {
      errorMessage.value = sizeError
      return
    }

    const contentsError = await validateFileContents(selectedFile.value)
    if (contentsError) {
      errorMessage.value = contentsError
      return
    }
  }

  isSubmitting.value = true
  try {
    if (isEditMode.value) {
      await submitEdit()
    } else {
      await submitUpload()
    }
    emit('saved')
    visibleRef.value = false
  } catch {
    const msg = isEditMode.value ? 'Failed to save changes. Please try again.' : 'Failed to upload file. Please try again.'
    errorMessage.value = msg
    emit('fileError', msg)
  } finally {
    isSubmitting.value = false
  }
}

function cancel(): void {
  visibleRef.value = false
}

function triggerFilePicker(): void {
  selectedFile.value = null
  fileTitle.value = ''
  errorMessage.value = ''
  isSubmitting.value = false
  triggerInputRef.value?.click()
  analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.OPENED_ADD_ATTACHMENT_MODAL, {
    attachment_type: ATTACHMENT_TYPE.FILE,
  })
}

async function openEditModal(): Promise<void> {
  selectedFile.value = null
  fileTitle.value = props.editAttachment?.content_title ?? ''
  errorMessage.value = ''
  isSubmitting.value = false
  visibleRef.value = false
  await nextTick()
  visibleRef.value = true
}

defineExpose({ triggerFilePicker, openEditModal })
</script>

<template>
  <template>
  <!-- Hidden file input to programmatically trigger browser's file picker.
       Browsers require a real DOM element click to open the file picker.
       The modal opens only after file selection via onTriggerFileChange. -->
  <input
    ref="triggerInputRef"
    type="file"
    :accept="ALLOWED_FILE_ACCEPT"
    class="sr-only"
    aria-hidden="true"
    tabindex="-1"
    aria-label="File upload trigger"
    @change="onTriggerFileChange"
  />

  <GbDialog
    :dialogId="dialogId"
    v-model:visible="visibleRef"
    :show-header-content="true"
    :footer-buttons="false"
    :show-close-icon="true"
    :pt="{ root: { class: '!w-[608px]' } }"
    @hide="cancel"
  >
    <template #headerContent>
      <div class="flex items-center gap-2 min-w-0">
        <span class="text-xl font-normal whitespace-nowrap">{{ isEditMode ? 'Edit file in' : 'Upload file to' }}</span>
        <span class="material-icons-round text-gray-800 shrink-0">folder</span>
        <span class="text-xl font-bold truncate min-w-0">{{ props.folderName }}</span>
      </div>
    </template>

    <template #bodyContent>
      <div class="flex flex-col items-center gap-6 -mx-6 -mb-6">

        <div class="flex flex-col gap-6 w-[522px]">

          <div class="flex flex-col gap-2">
            <p class="font-sans text-[14px] font-normal leading-5 text-gray-800">Selected file</p>
            <div class="flex items-center h-[39px] px-3 py-2">
              <span class="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-sans text-[14px] font-medium leading-5 text-[#4b5563]">
                {{ selectedFile?.name ?? (isEditMode ? 'No new file selected' : '') }}
              </span>
              <button type="button" :aria-label="isEditMode ? 'Replace file' : 'Change file'" class="ml-3 shrink-0 font-sans text-[14px] font-normal text-[#00769f] bg-transparent border-0 cursor-pointer p-0" @click="triggerFileInput">
                {{ isEditMode ? 'Replace' : 'Change' }}
              </button>
            </div>
            <input
              ref="fileInputRef"
              type="file"
              :accept="ALLOWED_FILE_ACCEPT"
              class="sr-only"
              aria-label="Select file to upload"
              tabindex="-1"
              @change="onFileChange"
            />
          </div>

          <div
            v-if="errorMessage"
            class="flex items-start gap-2 rounded bg-[#fdf2f2] px-3 py-2 text-[14px]"
            style="color: var(--text-toolkit-danger-default, #a94442)"
          >
            <span class="material-icons-outlined shrink-0 text-base mt-[1px]">warning_amber</span>
            <span>{{ errorMessage }}</span>
          </div>

          <div class="flex flex-col gap-2">
            <p class="font-sans text-[14px] font-normal leading-5 text-gray-800">Title</p>
            <input
              v-model="fileTitle"
              aria-label="File title"
              type="text"
              class="focus:outline-none focus:ring-1 focus:ring-[#00769f] w-full h-[39px] rounded border border-gray-300 bg-white px-3 py-2 font-sans text-[14px] font-medium leading-5 text-[#4b5563]"
              :placeholder="selectedFile?.name ?? 'Title'"
            />
          </div>

          <div class="flex items-start gap-2 rounded bg-[#e0f5ff] p-2">
            <span class="material-icons-round shrink-0 text-2xl leading-6 text-[#00769f]">info_outline</span>
            <p class="flex-1 min-w-0 font-sans text-[14px] leading-5 text-gray-800">
              <span class="font-bold">Note:</span><span class="font-normal"> Files you upload will be accessible to everyone with access to this folder.</span>
            </p>
          </div>

        </div>

        <div class="flex w-[608px] px-[43px] pt-6 pb-1 justify-end items-center gap-3 border-t border-gray-300">
          <GbButton type="button" gbTheme="grayOutlineSecondary" label="Cancel" @click="cancel" />
          <GbButton type="button" gbTheme="bluePrimary" :disabled="isSaveDisabled" @click="submit">
            <span>{{ isEditMode ? 'Edit' : 'Upload' }}</span>
          </GbButton>
        </div>

      </div>
    </template>

  </GbDialog>
  </template>
</template>
